
import { useState } from "react";
import { UnifiedTableLayout } from "@/components/tables/unified";
import { Edit, Trash2 } from "lucide-react";
import HardwareDialog from "@/components/hardware/HardwareDialog";
import { useHardwareAdmin } from "./useHardwareAdmin";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Hardware } from "@/types";
import { exportHardwareToCSV, exportHardwareTemplate } from "@/utils/hardwareUtils";
import RouteErrorBoundary from "@/components/error/RouteErrorBoundary";

const HardwareAdmin: React.FC = () => {
  const {
    hardware,
    fetchHardware,
    isLoading,
    error
  } = useHardwareAdmin();

  const [selectedHardware, setSelectedHardware] = useState<Hardware | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [hardwareToDelete, setHardwareToDelete] = useState<string | null>(null);
  const { toast } = useToast();

  const handleEdit = (hardware: Hardware) => {
    console.log("[HardwareAdmin] Edit hardware:", hardware.id);
    setSelectedHardware(hardware);
    setIsDialogOpen(true);
  };

  const handleDelete = async (hardwareId: string) => {
    console.log("[HardwareAdmin] Delete hardware:", hardwareId);
    setHardwareToDelete(hardwareId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!hardwareToDelete) return;
    
    try {
      console.log("[HardwareAdmin] Confirming delete hardware:", hardwareToDelete);
      const { error } = await supabase.from('hardware').delete().eq('id', hardwareToDelete);
      
      if (error) throw error;
      
      toast({ title: "Hardware deleted successfully" });
    } catch (error) {
      console.error("[HardwareAdmin] Error deleting hardware:", error);
      toast({ 
        title: "Error deleting hardware", 
        description: "An error occurred while deleting. Please try again.",
        variant: "destructive" 
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setHardwareToDelete(null);
    }
  };

  const handleCreate = () => {
    console.log("[HardwareAdmin] Create new hardware");
    setSelectedHardware(null);
    setIsDialogOpen(true);
  };

  const handleSaveHardware = () => {
    console.log("[HardwareAdmin] Saving hardware");
    fetchHardware();
    setIsDialogOpen(false);
  };

  const handleCSVImport = async (rows: any[]): Promise<void> => {
    try {
      toast({ 
        title: "Processing CSV import", 
        description: `Processing ${rows.length} rows...` 
      });
      
      // Mock import for now
      toast({ title: "CSV imported successfully", description: `Added ${rows.length} hardware items` });
    } catch (err: any) {
      toast({ 
        title: "Import failed", 
        description: err.message || "Unknown error occurred", 
        variant: "destructive" 
      });
      throw err;
    }
  };

  const handleExport = async () => {
    toast({ title: "CSV export coming soon!" });
  };

  const handleExportTemplate = async () => {
    toast({ title: "Template export coming soon!" });
  };

  const tableConfig = {
    tableName: "hardware",
    pageTitle: "Hardware Inventory",
    primaryKeyField: "id",
    columns: [
      {
        key: "manufacture",
        header: "Manufacture",
        sortable: true,
        filterable: true
      },
      {
        key: "product",
        header: "Product",
        sortable: true,
        filterable: true
      },
      {
        key: "part_number",
        header: "Part Number",
        sortable: true,
        filterable: true
      },
      {
        key: "product_type",
        header: "Product Type",
        sortable: true,
        filterable: true,
        type: "select" as const,
        options: ["battery", "dab pen", "white", "panel", "inverter", "bms", "other"]
      },
      {
        key: "description",
        header: "Description",
        sortable: true,
        filterable: true,
        render: (value) => (
          <div className="max-w-[300px] truncate" title={value}>{value || '-'}</div>
        )
      }
    ],
    rowActions: [
      {
        label: "Edit",
        icon: Edit,
        onClick: handleEdit
      },
      {
        label: "Delete",
        icon: Trash2,
        onClick: (row: Hardware) => handleDelete(row.id)
      }
    ],
    defaultSortColumn: "manufacture",
    searchFields: ["manufacture", "product", "part_number", "description"],
    addButtonText: "Add Hardware",
    onRowClick: () => {} // No specific row click action needed
  };

  return (
    <RouteErrorBoundary>
      <UnifiedTableLayout
        title={tableConfig.pageTitle}
        data={hardware}
        columns={tableConfig.columns}
        searchFields={tableConfig.searchFields}
        isLoading={isLoading}
        onAddNew={handleCreate}
        onImport={() => handleCSVImport([])}
        onExport={handleExport}
        onExportTemplate={handleExportTemplate}
        onRefresh={fetchHardware}
        rowActions={tableConfig.rowActions}
        onRowClick={tableConfig.onRowClick}
        addButtonText={tableConfig.addButtonText}
      />
      
      <HardwareDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        hardware={selectedHardware}
        onSave={handleSaveHardware}
        existingHardwareNames={hardware.map(h => h.manufacture)}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the hardware item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RouteErrorBoundary>
  );
};

export default HardwareAdmin;
